import { useState } from "react";
import { Link } from "react-router-dom";
import { HelpCircle, Book, MessageSquare, Mail, ExternalLink, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const faqs = [
  {
    question: "How do I post a job listing?",
    answer: "To post a job, navigate to the employer dashboard and click 'Post a Job'. Fill in the required information including job title, description, requirements, and salary range. Once submitted, your listing will be reviewed and published."
  },
  {
    question: "How can I apply for a job?",
    answer: "Browse available jobs in the Jobs section. Click on any job listing to view details, then click 'Apply Now'. You'll need to upload your resume and optionally include a cover letter. Track your applications in 'My Applications'."
  },
  {
    question: "How do I create an event?",
    answer: "Event organizers can submit events through the 'Submit Event' page. Choose your package type (Free, Featured, or Dedicated), provide event details, and complete the payment process if applicable."
  },
  {
    question: "What are the different user roles?",
    answer: "The platform has several roles: Job Seekers can apply for jobs and save listings; Employers can post jobs and manage applications; Event Organizers can submit and manage Bitcoin events; Admins have full platform access."
  },
  {
    question: "How do I change my profile settings?",
    answer: "Click your avatar in the top-right corner and select 'Profile' or 'Settings'. You can update your display name, avatar, bio, and notification preferences."
  },
  {
    question: "What payment methods are accepted?",
    answer: "We accept cryptocurrency payments through our secure payment processor. You'll receive a payment link upon submitting a job posting or event submission."
  },
];

const guides = [
  {
    title: "Getting Started Guide",
    description: "Learn the basics of using the platform",
    icon: Book,
    link: "/learn"
  },
  {
    title: "Employer Guide",
    description: "How to post jobs and manage applications",
    icon: Book,
    link: "/faq"
  },
  {
    title: "Job Seeker Guide",
    description: "Tips for finding and applying to jobs",
    icon: Book,
    link: "/faq"
  },
  {
    title: "Event Organizer Guide",
    description: "Submit and promote your Bitcoin events",
    icon: Book,
    link: "/faq"
  },
];

export const HelpWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredFaqs = faqs.filter(
    faq =>
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      {/* Floating Help Button */}
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-all z-40"
        size="icon"
        aria-label="Open help"
      >
        <HelpCircle className="h-6 w-6" />
      </Button>

      {/* Help Sidebar */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Help & Support</SheetTitle>
            <SheetDescription>
              Find answers, guides, and get support
            </SheetDescription>
          </SheetHeader>

          <Tabs defaultValue="faq" className="mt-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="faq">FAQs</TabsTrigger>
              <TabsTrigger value="guides">Guides</TabsTrigger>
              <TabsTrigger value="support">Support</TabsTrigger>
            </TabsList>

            {/* FAQs Tab */}
            <TabsContent value="faq" className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search FAQs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              <Accordion type="single" collapsible className="w-full">
                {filteredFaqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              {filteredFaqs.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No FAQs found matching your search.
                </div>
              )}
            </TabsContent>

            {/* Guides Tab */}
            <TabsContent value="guides" className="space-y-4">
              <div className="grid gap-4">
                {guides.map((guide, index) => (
                  <Link key={index} to={guide.link} className="block">
                    <Card className="cursor-pointer hover:bg-accent transition-colors">
                      <CardHeader className="flex flex-row items-center space-y-0 gap-4">
                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <guide.icon className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-base">{guide.title}</CardTitle>
                          <CardDescription className="text-sm">
                            {guide.description}
                          </CardDescription>
                        </div>
                        <ExternalLink className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                    </Card>
                  </Link>
                ))}
              </div>
            </TabsContent>

            {/* Support Tab */}
            <TabsContent value="support" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    Contact Support
                  </CardTitle>
                  <CardDescription>
                    Need help? Our support team is here for you.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button className="w-full justify-start" variant="outline" asChild>
                    <a href="/contact">
                      <Mail className="mr-2 h-4 w-4" />
                      Send us a message
                    </a>
                  </Button>
                  
                  <div className="space-y-2 pt-4 border-t">
                    <h4 className="font-medium text-sm">Support Hours</h4>
                    <p className="text-sm text-muted-foreground">
                      Monday - Friday: 9:00 AM - 6:00 PM UTC
                    </p>
                    <p className="text-sm text-muted-foreground">
                      We typically respond within 24 hours
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Quick Links</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button variant="ghost" className="w-full justify-start" asChild>
                    <a href="/about">
                      About Us
                    </a>
                  </Button>
                  <Button variant="ghost" className="w-full justify-start" asChild>
                    <a href="/terms-of-use">
                      Terms of Use
                    </a>
                  </Button>
                  <Button variant="ghost" className="w-full justify-start" asChild>
                    <a href="/privacy-policy">
                      Privacy Policy
                    </a>
                  </Button>
                  <Button variant="ghost" className="w-full justify-start" asChild>
                    <a href="/faq">
                      Full FAQ Page
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>
    </>
  );
};
